<template>
  <div class="tactical-board-wrapper">
    <!-- View Mode: Read-only preview -->
    <div v-if="!isEditing" class="board-view-mode">
      <div class="board-meta">
        <h3 class="board-title">
          <i class="bi bi-easel2"></i>
          {{ board.title || 'Tactical Board' }}
        </h3>
        <span v-if="board.category" class="board-category-badge" :class="'cat-' + board.category">
          {{ board.category }}
        </span>
      </div>
      <p v-if="board.description" class="board-description">{{ board.description }}</p>

      <!-- Read-only canvas preview -->
      <div class="board-preview-container">
        <TacticalBoardCanvas :title="board.title" :notes="board.notes || ''" :savedElements="board.elements || []"
          :background="board.background || 'full-pitch'" :savedBackgroundImage="board.background_image || ''"
          :orientation="board.orientation || 'portrait'"
          :imageTransform="board.image_transform || { x: 0, y: 0, scale: 1 }" :editMode="false" />
      </div>

      <!-- Action buttons -->
      <div v-if="showActions" class="board-actions">
        <button class="btn-board btn-edit" @click="startEdit" title="Edit board">
          <i class="bi bi-pencil"></i> Edit
        </button>
        <button class="btn-board btn-duplicate" @click="$emit('duplicate', board)" title="Duplicate board">
          <i class="bi bi-copy"></i> Duplicate
        </button>
        <button class="btn-board btn-delete" @click="confirmDelete" title="Delete board">
          <i class="bi bi-trash3"></i> Delete
        </button>
      </div>
    </div>

    <!-- Edit Mode: Full editor -->
    <div v-else class="board-edit-mode">
      <!-- Edit form fields -->
      <div class="edit-form-row">
        <div class="form-field">
          <label>Title</label>
          <input type="text" v-model="editData.title" class="form-input" placeholder="Board title" />
        </div>
        <div class="form-field">
          <label>Category</label>
          <select v-model="editData.category" class="form-input">
            <option value="general">General</option>
            <option value="training">Training</option>
            <option value="rtp">Return to Play</option>
            <option value="set-piece">Set Piece</option>
            <option value="formation">Formation</option>
          </select>
        </div>
      </div>
      <div class="form-field">
        <label>Description</label>
        <input type="text" v-model="editData.description" class="form-input"
          placeholder="Short description (optional)" />
      </div>

      <!-- Canvas editor -->
      <div class="board-editor-container">
        <TacticalBoardCanvas ref="editorCanvas" :title="editData.title" :notes="editData.notes"
          :savedElements="editData.elements" :background="editData.background"
          :savedBackgroundImage="editData.background_image"
          :orientation="editData.orientation" :imageTransform="editData.image_transform" :editMode="true"
          @update:elements="editData.elements = $event" @update:title="editData.title = $event"
          @update:notes="editData.notes = $event" @update:background="editData.background = $event"
          @update:backgroundImage="editData.background_image = $event"
          @update:orientation="editData.orientation = $event"
          @update:imageTransform="editData.image_transform = $event" />
      </div>

      <!-- Save/Cancel buttons -->
      <div class="edit-actions">
        <button class="btn-board btn-cancel" @click="cancelEdit">
          <i class="bi bi-x-lg"></i> Cancel
        </button>
        <button class="btn-board btn-save" @click="saveBoard" :disabled="saving">
          <i class="bi bi-check-lg"></i> {{ saving ? 'Saving...' : (board.id ? 'Update' : 'Save') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import TacticalBoardCanvas from './TacticalBoardCanvas.vue';
import { TacticalBoardService } from '@/services/tactical-board-service';
import { mapGetters } from 'vuex';

export default {
  name: 'TacticalBoard',
  components: { TacticalBoardCanvas },
  props: {
    board: {
      type: Object,
      default: () => ({
        id: null,
        title: 'Tactical Board',
        description: '',
        category: 'general',
        elements: [],
        notes: '',
        background: 'full-pitch',
        background_image: '',
        orientation: 'portrait',
        image_transform: { x: 0, y: 0, scale: 1 },
      })
    },
    showActions: { type: Boolean, default: true },
    startInEditMode: { type: Boolean, default: false },
  },
  emits: ['saved', 'deleted', 'duplicate', 'cancelled'],
  data() {
    return {
      isEditing: this.startInEditMode,
      saving: false,
      editData: this.cloneBoard(this.board),
    };
  },
  computed: {
    ...mapGetters(['getProfile']),
  },
  watch: {
    board: {
      handler(val) {
        if (!this.isEditing) {
          this.editData = this.cloneBoard(val);
        }
      },
      deep: true,
    },
    startInEditMode(val) {
      this.isEditing = val;
    },
  },
  methods: {
    cloneBoard(b) {
      return {
        id: b.id || null,
        title: b.title || 'Tactical Board',
        description: b.description || '',
        category: b.category || 'general',
        elements: b.elements ? JSON.parse(JSON.stringify(b.elements)) : [],
        notes: b.notes || '',
        background: b.background || 'full-pitch',
        background_image: b.background_image || '',
        orientation: b.orientation || 'portrait',
        image_transform: b.image_transform ? { ...b.image_transform } : { x: 0, y: 0, scale: 1 },
      };
    },

    startEdit() {
      this.editData = this.cloneBoard(this.board);
      this.isEditing = true;
    },

    cancelEdit() {
      this.isEditing = false;
      this.editData = this.cloneBoard(this.board);
      this.$emit('cancelled');
    },

    captureThumbnail() {
      // Serialize the live SVG canvas to a data-URI so lists/cards can show
      // a cheap <img> instead of mounting a full canvas component per board
      try {
        const canvasComp = this.$refs.editorCanvas;
        const svg = canvasComp && canvasComp.$refs && canvasComp.$refs.svgCanvas;
        if (!svg) return null;
        const clone = svg.cloneNode(true);
        clone.removeAttribute('class');
        clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        const xml = new XMLSerializer().serializeToString(clone);
        const bytes = new TextEncoder().encode(xml);
        let binary = '';
        bytes.forEach((b) => (binary += String.fromCharCode(b)));
        return 'data:image/svg+xml;base64,' + btoa(binary);
      } catch (err) {
        console.error('Error capturing board thumbnail:', err);
        return null;
      }
    },

    async saveBoard() {
      if (this.saving) return;
      this.saving = true;

      try {
        const profile = this.getProfile || {};
        const payload = {
          title: this.editData.title,
          description: this.editData.description,
          category: this.editData.category,
          elements: this.editData.elements,
          notes: this.editData.notes,
          background: this.editData.background,
          background_image: this.editData.background_image,
          orientation: this.editData.orientation,
          image_transform: this.editData.image_transform,
          thumbnail: this.captureThumbnail(),
        };

        let result;
        if (this.editData.id) {
          // Update existing board
          result = await TacticalBoardService.updateTacticalBoard(this.editData.id, payload);
        } else {
          // Create new board
          payload.clubId = profile.clubId;
          payload.created_by = profile.id;
          result = await TacticalBoardService.saveTacticalBoard(payload);
        }

        if (result.data.success) {
          this.isEditing = false;
          this.$emit('saved', {
            ...this.editData,
            id: result.data.board_id || this.editData.id,
          });
        } else {
          alert(result.data.message || 'Error saving board');
        }
      } catch (err) {
        console.error('Error saving tactical board:', err);
        alert('Failed to save tactical board');
      } finally {
        this.saving = false;
      }
    },

    confirmDelete() {
      if (!confirm('Delete this tactical board?')) return;
      this.$emit('deleted', this.board);
    },
  },
};
</script>

<style scoped>
.tactical-board-wrapper {
  width: 100%;
}

/* ---- View Mode ---- */
.board-view-mode {
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  border-radius: 12px;
  overflow: hidden;
}

.board-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px 0;
}

.board-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #f1f5f9;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.board-title i {
  color: #667eea;
}

.board-category-badge {
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 600;
}

.cat-training {
  background: rgba(59, 130, 246, 0.2);
  color: #60a5fa;
}

.cat-rtp {
  background: rgba(34, 197, 94, 0.2);
  color: #4ade80;
}

.cat-set-piece {
  background: rgba(249, 115, 22, 0.2);
  color: #fb923c;
}

.cat-formation {
  background: rgba(168, 85, 247, 0.2);
  color: #c084fc;
}

.cat-general {
  background: rgba(148, 163, 184, 0.2);
  color: #94a3b8;
}

.board-description {
  font-size: 0.8rem;
  color: #94a3b8;
  margin: 4px 20px 0;
}

.board-preview-container {
  min-height: 400px;
}

.board-actions {
  display: flex;
  gap: 8px;
  padding: 12px 20px 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

/* ---- Edit Mode ---- */
.board-edit-mode {
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  border-radius: 12px;
  padding: 20px;
}

.edit-form-row {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
}

.form-field {
  flex: 1;
  margin-bottom: 8px;
}

.form-field label {
  display: block;
  font-size: 0.7rem;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
}

.form-input {
  width: 100%;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: #e2e8f0;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 0.85rem;
  font-family: inherit;
}

.form-input:focus {
  outline: none;
  border-color: #667eea;
}

.form-input option {
  background: #1e293b;
}

.board-editor-container {
  min-height: 500px;
  margin: 12px 0;
}

.edit-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

/* ---- Buttons ---- */
.btn-board {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  font-family: inherit;
}

.btn-edit {
  background: rgba(102, 126, 234, 0.15);
  color: #667eea;
  border-color: rgba(102, 126, 234, 0.3);
}

.btn-edit:hover {
  background: rgba(102, 126, 234, 0.25);
}

.btn-duplicate {
  background: rgba(255, 255, 255, 0.06);
  color: #94a3b8;
}

.btn-duplicate:hover {
  background: rgba(255, 255, 255, 0.12);
  color: #e2e8f0;
}

.btn-delete {
  background: rgba(244, 63, 94, 0.1);
  color: #f87171;
  border-color: rgba(244, 63, 94, 0.2);
}

.btn-delete:hover {
  background: rgba(244, 63, 94, 0.2);
}

.btn-cancel {
  background: rgba(255, 255, 255, 0.06);
  color: #94a3b8;
}

.btn-cancel:hover {
  background: rgba(255, 255, 255, 0.12);
  color: #e2e8f0;
}

.btn-save {
  background: rgba(34, 197, 94, 0.2);
  color: #4ade80;
  border-color: rgba(34, 197, 94, 0.3);
}

.btn-save:hover:not(:disabled) {
  background: rgba(34, 197, 94, 0.3);
}

.btn-save:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
